import monetize from '@formatters/monetize';

import { DATE_FORMAT } from '@validators/date';
import { CPF_FORMAT } from '@validators/cpf';
import { ZIP_FORMAT } from '@validators/zip';
import { CELLPHONE_FORMAT } from '@validators/phone';

import BANKS, { BANK_CODES } from '@js/constants/banks';
import { BOOLEAN } from '@js/constants/options';

import { reduce } from '@/components/Form/javascripts/codes';

import { VEHICLE_SCHEMA } from '@/views/Vehicles/SCHEMA';

// import CONSTANTS from './form/constants';
import GROUPS from './javascripts/groups';

const SIMULATION_VEHICLE_SCHEMA = {
  ...VEHICLE_SCHEMA,
  credere_vehicle_model_id: VEHICLE_SCHEMA.vehicle_model_id,
};

delete SIMULATION_VEHICLE_SCHEMA.vehicle_model_id;

export const MINIMUM_FIELDS = [
  'lead[cpf_cnpj]',
  'lead[name]',
  'lead[phone_number]',
];

export const POSSIBLE_STEPS = [[
  ...MINIMUM_FIELDS,
  'lead[has_cnh]',
], [
  'lead[retrieve_gender]',
  'lead[birthdate]',
  'lead[email]',
], [
  'lead[retrieve_occupation]',
  'lead[monthly_income]',
  'lead[address][zip_code]',
], [
  'lead[address][state]',
  'lead[address][city]',
  'lead[address][district]',
  'lead[address][street]',
  'lead[address][number]',
  'lead[address][complement]',
]];

export function LEAD_REQUIRED(codes = BANK_CODES) {
  return reduce(codes, [
    [
      { rule: 'required' },
      { rule: 'required', field: 'simulation[store_id]' },
    ],
  ]);
}

export const REPEATING_RULES = {
  ALWAYS_VISIBLE: reduce(BANK_CODES, [
    [{ rule: 'required' }],
  ]),

  LICENSING_UF: reduce(BANK_CODES, [
    [{ rule: 'equal', field: 'simulation[vehicle][zero_km]', value: 'false' }],
  ]),

  VEHICLE_SEARCH_BY_LICENSE_PLATE_ENABLED: reduce(BANK_CODES, [
    [
      { rule: 'equal', field: 'simulation[vehicle][_enable_vehicle_search_by_license_plate]', value: 'true' },
      { rule: 'required', field: 'simulation[store_id]' },
    ],
  ]),

  CUSTOM_INSTALLMENTS_ENABLED: reduce(BANK_CODES, [
    [{ rule: 'equal', field: 'simulation[_condition][_enable_custom_installments]', value: 'true' }],
  ]),

  CUSTOM_SETTINGS_ENABLED: reduce(BANK_CODES, [
    [{ rule: 'equal', field: 'simulation[_condition][_enable_custom_settings]', value: 'true' }],
  ]),

  CUSTOM_INSTALLMENTS: reduce(BANK_CODES, [
    [{ rule: 'equal', field: 'simulation[_condition][_custom_installments_toggler]', value: 'true' }],
  ]),

  CUSTOM_SETTINGS: reduce(BANK_CODES, [
    [{ rule: 'equal', field: 'simulation[_condition][_custom_settings_toggler]', value: 'true' }],
  ]),

  LEAD_NONE: {},
  LEAD_REQUIRED: LEAD_REQUIRED(),
};

const SIMULATION_KEY = 'simulation';

export default {
  [SIMULATION_KEY]: {
    group: null,
    label: 'Simulação',
    description: 'Informações do financiamento',

    fields: {
      store_id: {
        group: null,
        label: 'Loja',
        description: 'Selecione',
        domain: 'stores',
        validations: [{
          type: 'blank',
        }],
        rules: REPEATING_RULES.ALWAYS_VISIBLE,
      },
      credere_seller_id: {
        group: null,
        label: 'Vendedor',
        description: 'Selecione',
        domain: 'seller',
        validations: [{
          type: 'blank',
        }],
        rules: REPEATING_RULES.ALWAYS_VISIBLE,
      },

      vehicle: {
        group: null,
        label: 'Veículo',
        description: 'Informações do automóvel',

        fields: {
          _enable_vehicle_search_by_license_plate: {
            group: null,
            options: BOOLEAN,
            rules: {},
          },

          _vehicle_search_by_license_plate: {
            group: null,
            label: 'Buscar dados do veículo pela placa',
            description: 'Exemplo: XXX-1234',
            validations: [],
            rules: REPEATING_RULES.VEHICLE_SEARCH_BY_LICENSE_PLATE_ENABLED,
          },

          ...SIMULATION_VEHICLE_SCHEMA,

          zero_km: {
            group: null,
            label: 'Veículo novo',
            description: 'O veículo é zero KM?',
            validations: [{
              type: 'inclusion',
              list: ['true', 'false'],
            }, {
              type: 'blank',
            }],
            options: BOOLEAN,
            rules: REPEATING_RULES.ALWAYS_VISIBLE,
          },
          credere_vehicle_id: {
            group: null,
            label: 'Identificador do veículo usado',
            description: 'Escolha um veículo usado',
            validations: [],
            options: [],
            rules: {},
          },
          licensing_uf: {
            group: null,
            label: 'Estado de licenciamento',
            description: 'Selecione',
            domain: 'states',
            validations: [{
              type: 'blank',
            }],
            rules: REPEATING_RULES.LICENSING_UF,
          },
          // licensing_city: {
          //   group: null,
          //   label: 'Cidade de licenciamento',
          //   description: 'Digite a cidade',
          //   validations: [{
          //     type: 'blank',
          //   }],
          //   rules: REPEATING_RULES.LICENSING_UF,
          // },

          asset_value: {
            group: null,
            label: 'Valor do veículo',
            description: 'R$ 0,00',
            mask: {
              type: 'monetize',
            },
            validations: [{
              type: 'greater_than',
              equal: true,
              count: 0,
              formatter: (value) => monetize(value, { prefix: '' }),
            }, {
              type: 'blank',
            }],
            rules: REPEATING_RULES.ALWAYS_VISIBLE,
          },
        },
      },

      commercial: {
        group: null,
        label: 'Veículo para uso comercial?',
        description: 'Uso comercial?',
        validations: [{
          type: 'inclusion',
          list: ['true', 'false'],
        }, {
          type: 'blank',
        }],
        options: BOOLEAN,
        rules: REPEATING_RULES.ALWAYS_VISIBLE,
      },

      documentation_value: {
        group: null,
        label: 'Valor da documentação',
        description: 'R$ 0,00',
        mask: {
          type: 'monetize',
        },
        validations: [],
        rules: REPEATING_RULES.ALWAYS_VISIBLE,
      },
      accessory_value: {
        group: null,
        label: 'Valor de acessórios',
        description: 'R$ 0,00',
        mask: {
          type: 'monetize',
        },
        validations: [],
        rules: REPEATING_RULES.ALWAYS_VISIBLE,
      },

      _condition: {
        group: null,
        label: 'Padrão das condições',
        description: 'Configurações avançadas da simulação',

        fields: {
          down_payment: {
            group: SIMULATION_KEY,
            label: 'Valor da entrada',
            description: 'R$ 0,00',
            mask: {
              type: 'monetize',
            },
            validations: [],
            rules: REPEATING_RULES.ALWAYS_VISIBLE,
          },

          _enable_custom_installments: {
            group: SIMULATION_KEY,
            options: BOOLEAN,
            rules: {},
          },

          _custom_installments_toggler: {
            group: SIMULATION_KEY,
            label: 'Simular com prazo específico?',
            description: 'Exemplo: Apenas 36 meses',
            validations: [{
              type: 'inclusion',
              list: ['true', 'false'],
            }],
            options: BOOLEAN,
            rules: REPEATING_RULES.CUSTOM_INSTALLMENTS_ENABLED,
          },

          installments: {
            group: SIMULATION_KEY,
            label: 'Prazo escolhido',
            description: 'Exemplo: 36',
            validations: [{
              type: 'installments',
              minimum: 1,
              maximum: 120,
            }],
            rules: REPEATING_RULES.CUSTOM_INSTALLMENTS,
          },

          _enable_custom_settings: {
            group: GROUPS.ADVANCED,
            options: BOOLEAN,
            rules: {},
          },

          _custom_settings_toggler: {
            group: GROUPS.ADVANCED,
            label: 'Editar controle comercial?',
            description: 'Alterar retorno padrão, etc?',
            validations: [{
              type: 'inclusion',
              list: ['true', 'false'],
            }],
            options: BOOLEAN,
            rules: REPEATING_RULES.CUSTOM_SETTINGS_ENABLED,
          },

          min_return: {
            group: GROUPS.ADVANCED,
            label: 'Retorno mínimo',
            description: 'Exemplo: 2',
            validations: [{
              type: 'not_a_number',
              blank: true,
            }, {
              type: 'greater_than_or_equal_to',
              count: 0,
            }, {
              type: 'less_than_or_equal_to',
              count: 99,
            }],
            rules: REPEATING_RULES.CUSTOM_SETTINGS,
          },
          max_return: {
            group: GROUPS.ADVANCED,
            label: 'Retorno máximo',
            description: 'Exemplo: 3',
            validations: [{
              type: 'not_a_number',
              blank: true,
            }, {
              type: 'greater_than_or_equal_to',
              count: 0,
            }, {
              type: 'less_than_or_equal_to',
              count: 99,
            }],
            rules: REPEATING_RULES.CUSTOM_SETTINGS,
          },
          return_preference: {
            group: GROUPS.ADVANCED,
            label: 'Preferência do retorno',
            description: 'Mínimo ou máximo?',
            validations: [],
            options: [{
              label: 'Retorno mínimo',
              value: 'min',
            }, {
              label: 'Retorno máximo',
              value: 'max',
            }],
            rules: REPEATING_RULES.CUSTOM_SETTINGS,
          },

          quota_preference: {
            group: GROUPS.ADVANCED,
            label: 'Preferência da parcela',
            description: 'Mínima ou máxima?',
            validations: [],
            options: [{
              label: 'Parcela mínima',
              value: 'min',
            }, {
              label: 'Parcela máxima',
              value: 'max',
            }],
            rules: REPEATING_RULES.CUSTOM_SETTINGS,
          },

          credit_condition_plus: {
            group: GROUPS.ADVANCED,
            label: 'Plus',
            description: 'Exemplo: 1',
            mask: {
              type: 'floatize',
            },
            validations: [{
              type: 'not_a_number',
              blank: true,
              float: true,
            }, {
              type: 'greater_than_or_equal_to',
              count: 0,
            }],
            rules: REPEATING_RULES.CUSTOM_SETTINGS,
          },

          include_financial_protection_insurance: {
            group: GROUPS.ADVANCED,
            label: 'Seguro de Proteção Financeira?',
            description: 'Incluir SPF?',
            validations: [],
            options: BOOLEAN,
            rules: REPEATING_RULES.CUSTOM_SETTINGS,
          },

          products_options: {
            group: GROUPS.ADVANCED,
            label: 'Outras preferências',
            description: 'Configurações da condição',

            fields: {
              include_capitalization_bond: {
                group: GROUPS.ADVANCED,
                label: 'Título de capitalização?',
                description: 'Incluir título de capitalização?',
                validations: [],
                options: BOOLEAN,
                rules: REPEATING_RULES.CUSTOM_SETTINGS,
              },
              include_asset_insurance: {
                group: GROUPS.ADVANCED,
                label: 'Seguro do bem?',
                description: 'Incluir seguro do bem?',
                validations: [],
                options: BOOLEAN,
                rules: REPEATING_RULES.CUSTOM_SETTINGS,
              },
              include_personal_insurance: {
                group: GROUPS.ADVANCED,
                label: 'Seguro de acidentes?',
                description: 'Incluir seguro de acidentes pessoais?',
                validations: [],
                options: BOOLEAN,
                rules: REPEATING_RULES.CUSTOM_SETTINGS,
              },
            },
          },

          _enable_exclusive_bank_fields: {
            group: GROUPS.ADVANCED,
            options: BOOLEAN,
            rules: {},
          },

          bank_coupon_code: {
            group: GROUPS.ADVANCED,
            label: 'Cupom',
            description: 'Exemplo: 1',
            validations: [],
            rules: reduce([
              BANKS.SANTANDER.code,
            ], [
              [
                { rule: 'equal', field: 'simulation[_condition][_enable_exclusive_bank_fields]', value: 'true' },
                { rule: 'required', field: 'simulation[store_id]' },
              ],
            ]),
          },

          grace_period_days: {
            group: GROUPS.ADVANCED,
            label: 'Carência',
            description: 'Exemplo: 30',
            validations: [{
              type: 'not_a_number',
              blank: true,
            }, {
              type: 'greater_than_or_equal_to',
              count: 0,
            }, {
              type: 'less_than_or_equal_to',
              count: 120,
            }],
            rules: reduce([
              BANKS.PAN.code,
            ], [
              [
                { rule: 'equal', field: 'simulation[_condition][_enable_exclusive_bank_fields]', value: 'true' },
                { rule: 'required', field: 'simulation[store_id]' },
              ],
            ]),
          },
        },
      },

      conditions: {
        group: null,
        label: 'Condições',
        description: 'Simulações individuais',

        fields: [],
      },

      webhook_url: {
        group: null,
        label: 'URL de Webhook',
        description: 'https://example.com/webhooks',
        validations: [],
        rules: {},
      },

      external_tracker_uuid: {
        group: null,
        label: 'Tracker externo',
        description: 'ID do tracker externo',
        validations: [],
        rules: {},
      },

      // payload: {
      //   group: null,
      //   label: 'Dados internos',
      //   description: 'Campos para identificação',

      //   fields: {
      //     referrer: {
      //       group: null,
      //       label: 'Referrer',
      //       description: 'URL do referrer',
      //       validations: [],
      //       rules: {},
      //     },
      //   },
      // },
    },
  },

  lead: {
    group: null,
    label: 'Pessoal',
    description: 'Informações do cliente',

    fields: {
      cpf_cnpj: {
        group: null,
        label: 'CPF',
        description: '999.999.999-99',
        mask: {
          type: 'patternize',
          format: CPF_FORMAT,
        },
        validations: [{
          type: 'cpf',
        }, {
          type: 'blank',
        }],
        rules: REPEATING_RULES.LEAD_REQUIRED,
      },
      name: {
        group: null,
        label: 'Nome',
        description: 'Nome completo',
        validations: [{
          type: 'blank',
        }],
        rules: REPEATING_RULES.LEAD_REQUIRED,
      },
      phone_number: {
        group: null,
        label: 'Celular',
        description: '(99) 99999-9999',
        mask: {
          type: 'patternize',
          format: CELLPHONE_FORMAT,
        },
        validations: [{
          type: 'cellphone',
        }, {
          type: 'blank',
        }],
        rules: REPEATING_RULES.LEAD_REQUIRED,
      },
      has_cnh: {
        group: null,
        label: 'Carteira de motorista?',
        description: 'Possui habilitação?',
        validations: [{
          type: 'inclusion',
          list: ['true', 'false'],
        }, {
          type: 'blank',
        }],
        options: BOOLEAN,
        rules: REPEATING_RULES.LEAD_NONE,
      },
      retrieve_gender: {
        group: null,
        label: 'Gênero',
        description: 'Selecione',
        domain: 'gender',
        validations: [{
          type: 'blank',
        }],
        rules: REPEATING_RULES.LEAD_NONE,
      },
      birthdate: {
        group: null,
        label: 'Data de nascimento',
        description: 'dd/mm/aaaa',
        mask: {
          type: 'patternize',
          format: DATE_FORMAT,
        },
        validations: [{
          type: 'date',
        }, {
          type: 'blank',
        }],
        rules: REPEATING_RULES.LEAD_NONE,
      },
      email: {
        group: null,
        label: 'E-mail',
        description: 'email@exemplo.com',
        validations: [{
          type: 'email',
        }, {
          type: 'blank',
        }],
        rules: REPEATING_RULES.LEAD_NONE,
      },
      retrieve_occupation: {
        group: null,
        label: 'Ocupação',
        description: 'Selecione',
        domain: 'occupation',
        validations: [{
          type: 'blank',
        }],
        rules: REPEATING_RULES.LEAD_NONE,
      },
      monthly_income: {
        group: null,
        label: 'Renda mensal',
        description: 'R$ 0,00',
        mask: {
          type: 'monetize',
        },
        validations: [{
          type: 'greater_than',
          count: 0,
        }, {
          type: 'blank',
        }],
        rules: REPEATING_RULES.LEAD_NONE,
      },
      address: {
        group: null,
        label: 'Endereço do cliente',
        description: 'Dados do principal endereço',

        fields: {
          zip_code: {
            group: null,
            label: 'CEP',
            description: '99999-999',
            mask: {
              type: 'patternize',
              format: ZIP_FORMAT,
            },
            validations: [{
              type: 'zip',
            }, {
              type: 'blank',
            }],
            rules: REPEATING_RULES.LEAD_NONE,
          },
          state: {
            group: null,
            label: 'Estado',
            description: 'Selecione',
            domain: 'states',
            validations: [{
              type: 'blank',
            }],
            rules: REPEATING_RULES.LEAD_NONE,
          },
          city: {
            group: null,
            label: 'Cidade',
            description: 'Cidade',
            validations: [{
              type: 'blank',
            }],
            rules: REPEATING_RULES.LEAD_NONE,
          },
          district: {
            group: null,
            label: 'Bairro',
            description: 'Bairro',
            validations: [{
              type: 'blank',
            }],
            rules: REPEATING_RULES.LEAD_NONE,
          },
          street: {
            group: null,
            label: 'Logradouro',
            description: 'Rua',
            validations: [{
              type: 'blank',
            }],
            rules: REPEATING_RULES.LEAD_NONE,
          },
          number: {
            group: null,
            label: 'Número',
            description: 'Exemplo: 1234',
            validations: [{
              type: 'blank',
            }],
            rules: REPEATING_RULES.LEAD_NONE,
          },
          complement: {
            group: null,
            label: 'Complemento',
            description: 'Exemplo: Apartamento 1234',
            validations: [],
            rules: REPEATING_RULES.LEAD_NONE,
          },
        },
      },
    },
  },
};
